import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { ArrowLeft, Presentation, Loader2, Sparkles, Copy, Check } from "lucide-react";

const DURATIONS = [5, 10, 20];

const INSTITUTION_TYPES = ["School", "International School", "University", "College", "Activity Center", "Coaching", "Preschool"];
const CURRICULUMS = ["CBSE", "ICSE", "State Board", "IB", "IGCSE / Cambridge", "A-Levels", "AP", "US Common Core", "UK National Curriculum", "Other"];
const COUNTRIES = ["India", "UAE", "United States", "United Kingdom", "Singapore", "Canada", "Australia", "Other"];

export default function GrowthDemoLesson() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    subject: "Science",
    grade: "Grade 8",
    topic: "",
    duration_min: 10,
    role: "School Teacher",
    institution_type: "School",
    curriculum: "CBSE",
    country: "India",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!form.subject?.trim()) { toast.error("Subject is required"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/growth/demo-lesson", form);
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not generate. Try again.");
      }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.content) return;
    try { await navigator.clipboard.writeText(result.content); setCopied(true); toast.success("Script copied"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Demo Lesson Prep">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="demo-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-amber inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Presentation className="h-3 w-3" /> Demo Lesson Prep
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          A perfect demo lesson script, ready to deliver
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Pick the subject, grade, duration, curriculum and country — TeeBee Intelligence drafts the full script
          (objectives, hook, board explanation, activity, recap, anticipated Q&A).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 tb-card p-5 self-start lg:sticky lg:top-6" data-testid="demo-form">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Inputs</p>

          <div className="mt-4 space-y-3">
            <Field label="Subject">
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-10 rounded-xl" data-testid="demo-subject" />
            </Field>
            <Field label="Grade">
              <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="h-10 rounded-xl" data-testid="demo-grade" />
            </Field>
            <Field label="Topic (optional)">
              <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Auto-pick if left blank" className="h-10 rounded-xl" data-testid="demo-topic" />
            </Field>
            <Field label="Duration">
              <div className="flex gap-2" data-testid="demo-duration">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setForm({ ...form, duration_min: d })}
                    className={`flex-1 h-10 rounded-xl text-sm font-medium border transition ${
                      form.duration_min === d ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                    }`}
                    data-testid={`demo-duration-${d}`}
                  >{d} min</button>
                ))}
              </div>
            </Field>
            <Field label="Role">
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-xl" data-testid="demo-role" />
            </Field>
            <Field label="Institution">
              <Select value={form.institution_type} options={INSTITUTION_TYPES} onChange={(v) => setForm({ ...form, institution_type: v })} testid="demo-inst" />
            </Field>
            <Field label="Curriculum">
              <Select value={form.curriculum} options={CURRICULUMS} onChange={(v) => setForm({ ...form, curriculum: v })} testid="demo-curr" />
            </Field>
            <Field label="Country">
              <Select value={form.country} options={COUNTRIES} onChange={(v) => setForm({ ...form, country: v })} testid="demo-country" />
            </Field>

            <div className="flex items-center justify-between rounded-xl bg-muted/40 px-3 py-2 border border-border">
              <p className="text-xs">Exact cost: <span className="font-medium">3 coins</span></p>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">3 🪙</span>
            </div>

            <Button onClick={generate} disabled={busy} className="w-full h-11 rounded-full" data-testid="demo-generate">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              {busy ? "Drafting script…" : "Generate demo lesson"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-7" data-testid="demo-result">
          {busy && <Skeleton className="h-72" />}
          {!busy && !result && (
            <div className="tb-card p-8 text-center">
              <Presentation className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Configure on the left, then your full demo lesson script appears here.</p>
            </div>
          )}
          {result && (
            <div className="tb-card p-5 tb-fade-up">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Demo lesson script · {form.duration_min} min</p>
                <Button variant="ghost" size="sm" onClick={copy} className="h-7 px-2 text-xs">
                  {copied ? <Check className="h-3 w-3 mr-1 text-emerald-500" /> : <Copy className="h-3 w-3 mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <Markdown>{result.content}</Markdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function Select({ value, options, onChange, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
